import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAdminFormPage } from '../../hooks/useAdminFormPage';
import { AdminFormPage } from '../../components/AdminFormPage';
import { getErpErrorMappingFormConfig } from '../../configs/erpErrorMappingFormConfig';
import { erpErrorMappingsService } from '../../api/erpErrorMappingsService';
import { erpRegistriesService } from '../../api/erpRegistriesService';
import { parseJsonField } from '../../utils/parseJsonField';
import type {
  CreateErpErrorMappingPayload,
  UpdateErpErrorMappingPayload,
} from '../../api/erpErrorMappingsService';

interface ErrorMappingParams {
  registrySlug?: string;
  mappingSlug?: string;
}

export function ErrorMappingFormPage() {
  const { registrySlug } = useParams<{ registrySlug: string }>();

  /* v8 ignore start */
  const registryHref = `/admin/erp/registries/${registrySlug ?? ''}`;
  /* v8 ignore stop */

  // ---- Fetch registry name for breadcrumb ----------------------------------

  const { data: registryResponse } = useQuery({
    queryKey: ['admin', 'erp', 'registries', registrySlug],
    queryFn: () => {
      /* v8 ignore start */
      if (!registrySlug) return Promise.reject(new Error('Registry slug is required'));
      /* v8 ignore stop */
      return erpRegistriesService.get(registrySlug);
    },
    enabled: !!registrySlug,
  });

  const registryName = registryResponse?.data?.name ?? 'Registry';

  // ---- Shared form page hook -----------------------------------------------

  const formPage = useAdminFormPage<unknown, ErrorMappingParams>({
    entityName: 'error mapping',
    getConfig: (mode, params) =>
      getErpErrorMappingFormConfig(mode, params.registrySlug ?? '', params.mappingSlug),
    getEditId: (params) => params.mappingSlug,
    queryKey: (params) => [
      'admin', 'erp', 'registries', params.registrySlug, 'error-mappings', params.mappingSlug,
    ],
    fetchFn: (params) => {
      /* v8 ignore start */
      if (!params.registrySlug || !params.mappingSlug)
        return Promise.reject(new Error('Registry slug and Mapping slug are required'));
      /* v8 ignore stop */
      return erpErrorMappingsService.get(params.registrySlug, params.mappingSlug);
    },
    fetchEnabled: (isEdit, params) => isEdit && !!params.registrySlug && !!params.mappingSlug,
    mapToInitialValues: (entity) => {
      const mapping = entity as Record<string, unknown>;
      return {
        slug: mapping.slug,
        priority: mapping.priority,
        match_type: mapping.match_type,
        match_config: JSON.stringify(mapping.match_config, null, 2),
        origami_error_code: mapping.origami_error_code,
        origami_error_message: mapping.origami_error_message,
        http_status: (mapping.http_status as number | null) ?? '',
        is_retryable: mapping.is_retryable,
        log_level: mapping.log_level,
      };
    },
    mutationFn: async (payload, isEdit, params) => {
      const matchConfig = parseJsonField(payload.match_config);

      const cleanedPayload = {
        ...payload,
        match_config: matchConfig ?? {},
        priority: payload.priority ? Number(payload.priority) : 0,
        http_status: payload.http_status ? Number(payload.http_status) : null,
      };

      if (isEdit && params.registrySlug && params.mappingSlug) {
        return erpErrorMappingsService.update(
          params.registrySlug,
          params.mappingSlug,
          cleanedPayload as UpdateErpErrorMappingPayload,
        );
      }
      if (params.registrySlug) {
        return erpErrorMappingsService.create(
          params.registrySlug,
          cleanedPayload as CreateErpErrorMappingPayload,
        );
      }
      throw new Error('Registry slug is required');
    },
    invalidateKey: (params) => [
      'admin', 'erp', 'registries', params.registrySlug, 'error-mappings',
    ],
    navigateBackPath: (params) => `/admin/erp/registries/${params.registrySlug ?? ''}`,
  });

  // ---- Breadcrumbs ---------------------------------------------------------

  const breadcrumbs = useMemo(() => [
    { label: 'Admin', href: '/admin' },
    { label: 'ERP Registries', href: '/admin/erp/registries' },
    { label: registryName, href: registryHref },
    { label: formPage.isEdit ? 'Edit Error Mapping' : 'Create Error Mapping' },
  ], [formPage.isEdit, registryName, registryHref]);

  const errorBreadcrumbs = useMemo(() => [
    { label: 'Admin', href: '/admin' },
    { label: 'ERP Registries', href: '/admin/erp/registries' },
    { label: registryName, href: registryHref },
    { label: 'Edit Error Mapping' },
  ], [registryName, registryHref]);

  return (
    <AdminFormPage
      formPage={formPage}
      entityLabel="Error Mapping"
      breadcrumbs={breadcrumbs}
      errorBreadcrumbs={errorBreadcrumbs}
      genericFetchErrorMessage="An unexpected error occurred."
    />
  );
}
